import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  TextInput,
} from 'react-native';
import { ms, s, vs } from '../../../utils/responsive';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../types/navigation';
import { BottomNavBar } from '../../../components/BottomNavBar';
import { vaultService } from '../../../services/vaultService';
import { useUserSelection } from '../../../contexts/UserSelectionContext';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('PasswordsDashboard');

type PasswordData = {
  id: string;
  category: string;
  passwordType: string;
  username: string;
  password: string;
  websiteUrl: string;
  notes: string;
};

type CategoryData = {
  id: string;
  name: string;
  icon: any;
  count: number;
  color: string;
  passwords: PasswordData[];
};

const categories: CategoryData[] = [
  { id: 'all', name: 'All', icon: require('../../../assets/icons/password.png'), count: 0, color: '#FFFFFF', passwords: [] },
  { id: 'financial', name: 'Financial', icon: require('../../../assets/icons/financial.png'), count: 0, color: '#29BFC0', passwords: [] },
  { id: 'social', name: 'Social', icon: require('../../../assets/icons/social.png'), count: 0, color: '#FFFFFF', passwords: [] },
  { id: 'utilities', name: 'Utilities', icon: require('../../../assets/icons/utilities.png'), count: 0, color: '#FFFFFF', passwords: [] },
  { id: 'communication', name: 'Communication', icon: require('../../../assets/icons/communication.png'), count: 0, color: '#FFFFFF', passwords: [] },
  { id: 'insurance', name: 'Insurance', icon: require('../../../assets/icons/insurance.png'), count: 0, color: '#FFFFFF', passwords: [] },
  { id: 'medical', name: 'Medical', icon: require('../../../assets/icons/medical.png'), count: 0, color: '#FFFFFF', passwords: [] },
  { id: 'online-accounts', name: 'Online Accounts', icon: require('../../../assets/icons/ecommerce.png'), count: 0, color: '#FFFFFF', passwords: [] },
  { id: 'memberships', name: 'Memberships', icon: require('../../../assets/icons/memberships.png'), count: 0, color: '#FFFFFF', passwords: [] },
  { id: 'security', name: 'Security', icon: require('../../../assets/icons/security.png'), count: 0, color: '#FFFFFF', passwords: [] },
  { id: 'digital', name: 'Digital', icon: require('../../../assets/icons/digital.png'), count: 0, color: '#FFFFFF', passwords: [] },
  { id: 'misc', name: 'Misc.', icon: require('../../../assets/icons/misc.png'), count: 0, color: '#FFFFFF', passwords: [] },
];

type CategoryCardProps = {
  item: CategoryData;
  isSelected: boolean;
  onPress: () => void;
  navigation: RootStackNavigationProp;
};

const CategoryCard: React.FC<CategoryCardProps> = ({ item, isSelected, onPress, navigation }) => {
  return (
    <TouchableOpacity
      style={[
        styles.categoryCard,
        { backgroundColor: isSelected ? '#29BFC0' : '#FFFFFF' }
      ]}
      onPress={onPress}
    >
      <View style={styles.categoryIconContainer}>
        <Image source={item.icon} style={styles.categoryIcon} />
      </View>
      <Text style={[
        styles.categoryName,
        { color: isSelected ? '#FFFFFF' : '#1C2C4F' }
      ]}>{item.name}</Text>
      <Text style={[
        styles.categoryCount,
        { color: isSelected ? '#FFFFFF' : '#6B7280' }
      ]}>{item.count} {item.count === 1 ? 'Password' : 'Passwords'}</Text>
      <TouchableOpacity
        style={[
          styles.addButton,
          { backgroundColor: isSelected ? '#FFFFFF' : '#29BFC0' }
        ]}
        onPress={() => navigation.navigate('PasswordsInputScreen' as any, { category: item.name })}
      >
        <Image
          source={require('../../../assets/icons/add.png')}
          style={[
            styles.addIcon,
            { tintColor: isSelected ? '#29BFC0' : '#FFFFFF' }
          ]}
        />
      </TouchableOpacity>
    </TouchableOpacity>
  );
};

type PasswordItemProps = {
  item: PasswordData;
  onPress: () => void;
};

const PasswordItem: React.FC<PasswordItemProps> = ({ item, onPress }) => {
  const getIcon = (category: string) => {
    const categoryLower = category.toLowerCase();
    switch (categoryLower) {
      case 'financial':
        return require('../../../assets/icons/financial.png');
      case 'social':
        return require('../../../assets/icons/social.png');
      case 'utilities':
        return require('../../../assets/icons/utilities.png');
      case 'communication':
        return require('../../../assets/icons/communication.png');
      case 'insurance':
        return require('../../../assets/icons/insurance.png');
      case 'medical':
        return require('../../../assets/icons/medical.png');
      case 'online-accounts':
      case 'ecommerce':
        return require('../../../assets/icons/ecommerce.png');
      case 'memberships':
        return require('../../../assets/icons/memberships.png');
      case 'security':
        return require('../../../assets/icons/security.png');
      case 'digital':
        return require('../../../assets/icons/digital.png');
      case 'misc':
      default:
        return require('../../../assets/icons/misc.png');
    }
  };

  return (
    <TouchableOpacity style={styles.passwordItem} onPress={onPress}>
      <View style={styles.passwordIcon}>
        <Image source={getIcon(item.category)} style={styles.passwordIconImage} />
      </View>
      <View style={styles.passwordInfo}>
        <Text style={styles.passwordName}>{item.passwordType}</Text>
        <Text style={styles.passwordUrl}>{item.username}</Text>
      </View>
      <View style={styles.passwordType}>
        <Text style={styles.passwordTypeText}>{item.category}</Text>
        <TouchableOpacity style={styles.moreButton}>
          <Text style={styles.moreButtonText}>•••</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
};

export const PasswordsDashboardScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute();
  const { isViewingOthersData, isDeathVerifiedForSelectedUser, selectedUserId, availablePrimaryUsers } = useUserSelection();
  const [, setAllPasswords] = useState<PasswordData[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [categoryData, setCategoryData] = useState<CategoryData[]>(categories);
  const [searchText, setSearchText] = useState('');

  // Get the name of the user being viewed
  const viewingUserName = isViewingOthersData
    ? availablePrimaryUsers.find(u => u.id === selectedUserId)?.name || 'User'
    : null;

  // Passwords require death verification when viewing another user's data
  const requiresDeathVerification = isViewingOthersData && !isDeathVerifiedForSelectedUser;

  // Handle route params for pre-selecting category
  useEffect(() => {
    const params = route.params as any;
    if (params?.selectedCategory) {
      setSelectedCategory(params.selectedCategory);
    }
  }, [route.params]);

  useFocusEffect(
    useCallback(() => {
      loadPasswords();
    }, [selectedUserId, isViewingOthersData, isDeathVerifiedForSelectedUser])
  );

  const loadPasswords = async () => {
    setLoading(true);
    try {
      // K5: another person's passwords are theirs (decrypted with their
      // sealed key) and only after a verified death; before that, nothing.
      if (requiresDeathVerification) {
        setAllPasswords([]);
        setCategoryData(categories);
        return;
      }
      const passwords = await vaultService.getAllPasswords(isViewingOthersData ? selectedUserId : undefined);
      logger.debug('Loaded passwords:', passwords);
      logger.debug('Password categories:', passwords.map(p => p.category));
      setAllPasswords(passwords);
      
      // Update category data with password counts
      const updatedCategories = categories.map(category => {
        if (category.id === 'all') {
          return {
            ...category,
            count: passwords.length,
            passwords: passwords,
          };
        } else {
          const categoryPasswords = passwords.filter(p => p.category.toLowerCase() === category.id.toLowerCase());
          logger.debug(`Category ${category.id}: ${categoryPasswords.length} passwords`);
          return {
            ...category,
            count: categoryPasswords.length,
            passwords: categoryPasswords,
          };
        }
      });
      
      setCategoryData(updatedCategories);
    } catch (error) {
      logger.error('Error loading passwords:', error);
      Alert.alert('Error', 'Failed to load passwords');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const handlePasswordPress = (password: PasswordData) => {
    navigation.navigate('PasswordsDataScreen' as any, {
      passwordId: password.id,
      targetUserId: isViewingOthersData ? selectedUserId : undefined,
      viewOnly: isViewingOthersData,
    });
  };

  const handleAddPassword = () => {
    navigation.navigate('PasswordsInputScreen' as any);
  };

  const selectedCategoryData = categoryData.find(cat => cat.id === selectedCategory);
  const displayPasswords = selectedCategoryData?.passwords || [];

  const filteredPasswords = displayPasswords.filter(password =>
    searchText === '' ||
    password.passwordType.toLowerCase().includes(searchText.toLowerCase()) ||
    password.username.toLowerCase().includes(searchText.toLowerCase()) ||
    password.category.toLowerCase().includes(searchText.toLowerCase())
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading passwords...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../assets/icons/back.png')}
              style={[styles.backIcon, { tintColor: '#333' }]}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.titleContainer}>
          <Text style={styles.manageText}>Manage</Text>
          <Text style={styles.allPasswordsText}>All Passwords</Text>
        </View>

        {/* Death Verification Required Banner for successors */}
        {requiresDeathVerification && (
          <View style={styles.deathVerificationBanner}>
            <Text style={styles.deathVerificationTitle}>Access Restricted</Text>
            <Text style={styles.deathVerificationText}>
              Password information for {viewingUserName} will only be accessible after a life-ending event has been verified.
            </Text>
            <Text style={styles.deathVerificationSubtext}>
              To gain access, you must submit a death certificate through the app and complete the verification process.
            </Text>
          </View>
        )}

        {/* Show content only if not requiring death verification */}
        {!requiresDeathVerification && (
          <>
            <View style={styles.searchContainer}>
              <TextInput
                style={styles.searchInput}
                placeholder="Search passwords"
                placeholderTextColor="#9CA3AF"
                value={searchText}
                onChangeText={setSearchText}
              />
              <Image
                source={require('../../../assets/icons/search.png')}
                style={styles.searchIcon}
              />
            </View>

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.categoriesList}
              contentContainerStyle={{ paddingRight: 30 }}
            >
              {categoryData.map((category) => (
                <CategoryCard
                  key={category.id}
                  item={category}
                  isSelected={selectedCategory === category.id}
                  onPress={() => handleCategorySelect(category.id)}
                  navigation={navigation}
                />
              ))}
            </ScrollView>

            <View style={styles.passwordsContainer}>
              {filteredPasswords.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyTitle}>
                    {selectedCategory === 'all' ? 'No Passwords Yet' : `No ${selectedCategoryData?.name} Passwords`}
                  </Text>
                  <Text style={styles.emptySubtitle}>
                    {selectedCategory === 'all'
                      ? 'Start by adding your first password to keep it safe and organized'
                      : `Add your first ${selectedCategoryData?.name.toLowerCase()} password`
                    }
                  </Text>
                  <TouchableOpacity style={styles.mainAddButton} onPress={handleAddPassword}>
                    <Text style={styles.addButtonText}>
                      {selectedCategory === 'all' ? 'Add Your First Password' : `Add ${selectedCategoryData?.name} Password`}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : (
                filteredPasswords.map((password) => (
                  <PasswordItem key={password.id} item={password} onPress={() => handlePasswordPress(password)} />
                ))
              )}
            </View>
          </>
        )}
      </ScrollView>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#1C2C4F',
    fontSize: ms(16, 0.3),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '25%',
  },
  notificationIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  searchContainer: {
    margin: ms(20),
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    paddingHorizontal: ms(15),
  },
  searchInput: {
    flex: 1,
    height: s(50),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  searchIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  titleContainer: {
    paddingHorizontal: ms(20),
    marginBottom: ms(15),
  },
  manageText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginBottom: ms(5),
  },
  allPasswordsText: {
    fontSize: ms(24, 0.3),
    color: '#FF9F5A',
    fontWeight: '600',
  },
  categoriesList: {
    paddingLeft: ms(20),
    marginBottom: ms(25),
    paddingBottom: ms(10),
  },
  passwordsContainer: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(120),
  },
  categoryCard: {
    width: s(130),
    height: s(160),
    borderRadius: ms(15),
    padding: ms(15),
    marginRight: ms(15),
    alignItems: 'center',
    justifyContent: 'space-between',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  categoryIconContainer: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#F3F6F8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#29BFC0',
  },
  categoryName: {
    fontSize: ms(14, 0.3),
    fontWeight: '500',
    textAlign: 'center',
  },
  categoryCount: {
    fontSize: ms(12, 0.3),
    textAlign: 'center',
  },
  addButton: {
    width: s(30),
    height: s(30),
    borderRadius: ms(15),
    alignItems: 'center',
    justifyContent: 'center',
  },
  addIcon: {
    width: s(15),
    height: s(15),
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(40),
    minHeight: s(400),
  },
  emptyIcon: {
    width: s(80),
    height: s(80),
    opacity: 0.5,
    marginBottom: ms(20),
  },
  emptyTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  emptySubtitle: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(30),
  },
  passwordItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(15),
    marginBottom: ms(15),
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  passwordIcon: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#29BFC0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  passwordIconImage: {
    width: s(25),
    height: s(25),
    tintColor: '#FFFFFF',
  },
  passwordInfo: {
    flex: 1,
    marginLeft: ms(15),
  },
  passwordName: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    fontWeight: '500',
    marginBottom: ms(5),
  },
  passwordUrl: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
  },
  passwordType: {
    alignItems: 'flex-end',
  },
  passwordTypeText: {
    fontSize: ms(14, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(5),
  },
  moreButton: {
    padding: ms(5),
  },
  moreButtonText: {
    fontSize: ms(20, 0.3),
    color: '#6B7280',
    letterSpacing: ms(2),
  },
  addButtonText: {
    fontSize: ms(18, 0.3),
    fontWeight: '500',
    color: '#FFFFFF',
  },
  mainAddButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    padding: ms(15),
    alignItems: 'center',
    marginTop: ms(20),
  },
  deathVerificationBanner: {
    backgroundColor: '#FEF3C7',
    borderWidth: ms(1),
    borderColor: '#F59E0B',
    borderRadius: ms(12),
    padding: ms(20),
    marginHorizontal: ms(20),
    marginVertical: ms(20),
  },
  deathVerificationTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#92400E',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  deathVerificationText: {
    fontSize: ms(14, 0.3),
    color: '#92400E',
    textAlign: 'center',
    marginBottom: ms(10),
    lineHeight: ms(20, 0.3),
  },
  deathVerificationSubtext: {
    fontSize: ms(12, 0.3),
    color: '#B45309',
    textAlign: 'center',
    lineHeight: ms(18, 0.3),
  },
});

export default PasswordsDashboardScreen;
